import api from './api';

// Types
export interface Campaign {
  ID: string;
  TITULO: string;
  DESCRICAO?: string;
  HISTORIA?: string;
  META_VALOR: number;
  VALOR_ARRECADADO: number;
  MOEDA?: string;
  IMAGEM?: string;
  IMAGEM_CAPA?: string;
  VIDEO_URL?: string;
  CATEGORIA?: string;
  CATEGORIA_NOME?: string;
  CATEGORIA_ICONE?: string;
  CATEGORIA_COR?: string;
  TIPO?: string;
  VISIBILIDADE?: string;
  ESTADO?: string;
  ORGANIZADOR_NOME?: string;
  ORGANIZADOR_FOTO?: string;
  BENEFICIARIO_NOME?: string;
  VERIFICADA?: boolean;
  DESTAQUE?: boolean;
  TRENDING?: boolean;
  TAGS?: string;
  SLUG?: string;
  DATA_INICIO?: string;
  DATA_FIM?: string;
  DATA_CRIACAO?: string;
  TOTAL_SHARES?: number;
  TOTAL_VIEWS?: number;
  TOTAL_COMENTARIOS?: number;
  TOTAL_DOADORES?: number;
  // Detailed data (from campaign detail endpoint)
  rewards?: Reward[];
  recentDonations?: Donation[];
  updates?: Update[];
  media?: Media[];
  // Legacy compat (old field names)
  META?: number;
  totalArrecadado?: number;
  totalDoadores?: number;
  progresso?: number;
  entidadeNome?: string;
  categoriaNome?: string;
  categoriaIcone?: string;
  doacoesRecentes?: Donation[];
  topDoadores?: Donation[];
  atualizacoes?: Update[];
  recompensas?: Reward[];
  comentarios?: Comment[];
}

export interface Donation {
  ID: string;
  VALOR: number;
  MENSAGEM?: string;
  ANONIMO?: boolean;
  DATA_DONATIVO: string;
  doadorNome?: string;
}

export interface Update {
  ID: string;
  TITULO: string;
  CONTEUDO: string;
  IMAGEM?: string;
  MIME_TYPE?: string;
  DATA_CRIACAO: string;
}

export interface Reward {
  ID: string;
  TITULO: string;
  DESCRICAO?: string;
  VALOR_MINIMO: number;
  QUANTIDADE_DISPONIVEL?: number;
  DATA_ENTREGA?: string;
  totalSelecionado?: number;
}

export interface Comment {
  ID: string;
  CONTEUDO: string;
  DATA_CRIACAO: string;
  autorNome?: string;
  respostas?: Comment[];
}

export interface Media {
  ID: string;
  TIPO: 'IMAGEM' | 'VIDEO';
  URL: string;
  LEGENDA?: string;
  ORDEM: number;
}

export interface Category {
  ID: string | number;
  CODIGO?: string;
  NOME: string;
  ICONE?: string;
  COR?: string;
  ORDEM?: number;
  totalCampanhas?: number;
  total_campanhas?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class DonationsService {
  /**
   * Get all active donation campaigns
   */
  async getCampaigns(params?: {
    search?: string;
    categoria?: string;
    destaque?: boolean;
    trending?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Campaign>> {
    const response = await api.get<PaginatedResponse<Campaign>>('/public/donations/campaigns', { params });
    return response.data;
  }

  /**
   * Get featured campaigns
   */
  async getFeaturedCampaigns(): Promise<Campaign[]> {
    const response = await api.get<{ success: boolean; data: { destaque: Campaign[]; trending: Campaign[]; quaseLa: Campaign[] } }>('/public/donations/featured');
    if (response.data.success && response.data.data) {
      const { destaque = [], trending = [] } = response.data.data;
      const seen = new Set<string>();
      return [...destaque, ...trending].filter(c => seen.has(c.ID) ? false : seen.add(c.ID) && true);
    }
    return [];
  }

  /**
   * Get campaign categories
   */
  async getCategories(): Promise<Category[]> {
    const response = await api.get<{ success: boolean; data: Category[] }>('/public/donations/categories');
    if (response.data.success) {
      return response.data.data;
    }
    return [];
  }

  /**
   * Get campaign details
   */
  async getCampaignDetails(campaignId: string): Promise<Campaign> {
    const response = await api.get<{ success: boolean; data: Campaign; error?: string }>(
      `/public/donations/campaigns/${campaignId}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes da campanha');
  }

  /**
   * Get campaign donors
   */
  async getCampaignDonors(
    campaignId: string,
    params?: { page?: number; limit?: number; sort?: 'recent' | 'amount' }
  ): Promise<PaginatedResponse<Donation>> {
    const response = await api.get<PaginatedResponse<Donation>>(
      `/public/donations/campaigns/${campaignId}/donors`,
      { params }
    );
    return response.data;
  }

  /**
   * Get campaign comments
   */
  async getCampaignComments(
    campaignId: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<Comment>> {
    const response = await api.get<PaginatedResponse<Comment>>(
      `/public/donations/campaigns/${campaignId}/comments`,
      { params }
    );
    return response.data;
  }

  /**
   * Share campaign
   */
  async shareCampaign(campaignId: string, platform: string): Promise<void> {
    await api.post(`/public/donations/campaigns/${campaignId}/share`, { plataforma: platform });
  }

  /**
   * Create a donation
   */
  async createDonation(data: {
    campanha_id: string;
    valor: number;
    doador_nome?: string;
    doador_email?: string;
    doador_telefone?: string;
    mensagem?: string;
    anonimo?: boolean;
  }): Promise<{ id: string; campanha: string; valor: number; estado: string }> {
    const response = await api.post<{ success: boolean; data: any; error?: string }>(
      '/public/donations/donate',
      data
    );
    if (response.data.success) return response.data.data;
    throw new Error(response.data.error || 'Erro ao criar doação');
  }

  /**
   * Initiate SISP payment for a donation (public — no auth required)
   */
  async initiateSispPayment(donationId: string, returnUrl?: string): Promise<{ paymentUrl: string; merchantRef: string }> {
    const response = await api.post<{ success: boolean; data: { paymentUrl: string; merchantRef: string }; error?: string }>(
      `/public/donations/${donationId}/sisp-initiate`,
      { returnUrl: returnUrl || `${window.location.origin}/payments/result?origem=doacao` }
    );
    if (response.data.success && response.data.data?.paymentUrl) return response.data.data;
    throw new Error(response.data.error || 'Erro ao iniciar pagamento SISP');
  }

  /**
   * Confirm donation payment
   */
  async confirmDonationPayment(donationId: string, data: {
    metodo_pagamento: string;
    referencia_pagamento?: string;
  }): Promise<void> {
    const response = await api.post<{ success: boolean; error?: string }>(
      `/public/donations/${donationId}/confirm-payment`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Erro ao confirmar pagamento');
  }

  /**
   * Get QR Code for campaign
   */
  getQRCodeUrl(campaignId: string, size: number = 200): string {
    const donateUrl = `https://ifundhere-sandbox.web.app/campanhas/${campaignId}/doar`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(donateUrl)}`;
  }

  /**
   * Get embed iframe code for campaign
   */
  getEmbedCode(campaignId: string): string {
    return `<iframe src="https://ifundhere-sandbox.web.app/campanhas/${campaignId}/doar" width="400" height="600" frameborder="0" style="border:none;border-radius:16px;"></iframe>`;
  }

  /**
   * Get donation progress percentage
   */
  getDonationProgress(campaign: { META_VALOR?: number; VALOR_ARRECADADO?: number; META?: number; totalArrecadado?: number }): number {
    const meta = Number(campaign.META_VALOR || campaign.META || 0);
    if (meta === 0) return 0;
    const arrecadado = Number(campaign.VALOR_ARRECADADO || campaign.totalArrecadado || 0);
    return Math.min(100, (arrecadado / meta) * 100);
  }

  formatProgress(progress: number | string | null | undefined): string {
    const p = Number(progress);
    if (!p || isNaN(p)) return '0%';
    if (p < 1) return `${p.toFixed(2)}%`;
    if (p < 10) return `${p.toFixed(1)}%`;
    return `${p.toFixed(0)}%`;
  }

  /**
   * Format currency (CVE)
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(value) + '$';
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('pt-CV', {
      timeZone: 'Atlantic/Cape_Verde',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Format relative time
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes <= 1 ? 'agora' : `há ${diffMinutes} minutos`;
      }
      return diffHours === 1 ? 'há 1 hora' : `há ${diffHours} horas`;
    } else if (diffDays === 1) {
      return 'ontem';
    } else if (diffDays < 7) {
      return `há ${diffDays} dias`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? 'há 1 semana' : `há ${weeks} semanas`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? 'há 1 mês' : `há ${months} meses`;
    } else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? 'há 1 ano' : `há ${years} anos`;
    }
  }

  // Legacy support - map old interface to new
  async getDonations(): Promise<Campaign[]> {
    const response = await this.getCampaigns();
    return response.data || [];
  }

  async getDonationDetails(donationId: string): Promise<Campaign> {
    return this.getCampaignDetails(donationId);
  }
}

export default new DonationsService();
